import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Navbar as UINavbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Badge
} from "@heroui/react";
import ThemeSwitch from './ThemeSwitch';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect with throttling for performance
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated links with active state
  const navLinks = [
    { name: 'Strona główna', path: '/' },
    { name: 'Usługi', path: '/uslugi' },
    { name: 'O nas', path: '/o-nas' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Kontakt', path: '/kontakt' }
  ];

  // Helper to check if link is active
  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleNavClick = () => {
    // Przewiń do góry po kliknięciu
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsMenuOpen(false);
  };

  // Navbar animation variants
  const navbarVariants = {
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.3 }
    },
    hidden: {
      y: -30,
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={navbarVariants}
      className="sticky top-0 z-50"
    >
      <UINavbar
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        className={`transition-all duration-300 ${
          scrolled
            ? 'bg-background/90 backdrop-blur-md shadow-md border-b border-divider/30'
            : 'bg-transparent'
        }`}
        classNames={{
          wrapper: "px-4 sm:px-6 lg:px-8 h-16 max-w-full"
        }}
      >
        <NavbarContent className="gap-0">
          <NavbarMenuToggle
            aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
            className="md:hidden"
            // Improved accessibility
            sr-only="Menu główne"
          />

          <NavbarBrand className="mr-8">
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link
                to="/"
                className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary text-transparent bg-clip-text"
                aria-label="Strona główna IT For Everyone"
                onClick={handleNavClick}
              >
                ITFE
                <Badge
                  content="IT For Everyone"
                  color="primary"
                  size="sm"
                  className="ml-2 hidden sm:inline-flex badge-logo"
                />
              </Link>
            </motion.div>
          </NavbarBrand>
        </NavbarContent>

        {/* Desktop Navigation with enhanced visual indicators */}
        <NavbarContent className="hidden md:flex gap-1" justify="center">
          {navLinks.map((link, index) => (
            <NavbarItem key={link.name}>
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.07 }}
              >
                <Link
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium relative group transition-colors ${
                    isActive(link.path)
                      ? 'text-primary font-semibold'
                      : 'text-foreground hover:text-primary'
                  }`}
                  aria-current={isActive(link.path) ? "page" : undefined}
                  onClick={handleNavClick}
                >
                  {link.name}
                  {/* Animated underline indicator */}
                  <motion.span
                    className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-secondary ${
                      isActive(link.path) ? 'w-full' : 'w-0'
                    } group-hover:w-full transition-all duration-300`}
                    layoutId="navbar-underline"
                  />
                </Link>
              </motion.div>
            </NavbarItem>
          ))}
        </NavbarContent>

        {/* Right side actions */}
        <NavbarContent justify="end" className="gap-2">
          {/* Theme toggle - Replace with ThemeSwitch component */}
          <NavbarItem className="hidden sm:flex">
            <ThemeSwitch />
          </NavbarItem>

          {/* Language selector */}
          <NavbarItem className="hidden sm:flex">
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="light"
                  isIconOnly
                  aria-label="Wybierz język"
                >
                  <span className="text-xs font-medium">PL</span>
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Dostępne języki">
                <DropdownItem key="pl" textValue="Polski" className="text-foreground" isSelected>Polski</DropdownItem>
               {/* <DropdownItem key="en" textValue="English" className="text-foreground">English</DropdownItem>*/}
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>

          {/* Contact button with improved visuals */}
          <NavbarItem>
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                as={Link}
                to="/kontakt"
                color="primary"
                variant={scrolled ? "solid" : "bordered"}
                className="font-medium text-sm"
                endContent={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                }
	  	          onPress={() => setIsMenuOpen(false)}
              >
                Konsultacja
              </Button>
            </motion.div>
          </NavbarItem>
        </NavbarContent>

        {/* Improved mobile menu with animations */}
        <NavbarMenu className="bg-background/95 backdrop-blur-md pt-6 pb-6 px-4">
          <div className="flex flex-col gap-5">
            <AnimatePresence>
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <NavbarMenuItem>
                    <Link
                      to={link.path}
                      className={`block w-full px-3 py-3 rounded-lg text-lg transition-colors ${
                        isActive(link.path)
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-default-100 hover:text-primary'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                      aria-current={isActive(link.path) ? "page" : undefined}
                    >
                      <span className="flex items-center">
                        {link.name}

                        {isActive(link.path) && (
                          <motion.span
                            className="ml-2"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 23 }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </motion.span>
                        )}
                      </span>
                    </Link>
                  </NavbarMenuItem>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Mobile-only controls - update theme toggle */}
            <motion.div
              className="mt-4 pt-4 border-t border-divider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex justify-between items-center px-3">
                <p className="text-sm text-foreground-muted">Ustawienia</p>

                <div className="flex gap-2">
                  {/* Replace with ThemeSwitch */}
                  <ThemeSwitch />

                  <Dropdown>
                    <DropdownTrigger>
                      <Button
                        variant="flat"
                        size="sm"
                        aria-label="Wybierz język"
                      >
                        <span className="text-xs font-medium">PL</span>
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Dostępne języki">
                      <DropdownItem key="pl" textValue="Polski" className="text-foreground" isSelected>Polski</DropdownItem>
                      <DropdownItem key="en" textValue="English" className="text-foreground">English</DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            </motion.div>
          </div>
        </NavbarMenu>
      </UINavbar>
    </motion.div>
  );
};

export default Navbar;